import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { BEST_SELLER, PRODUCT_FEATURES, IMAGES, CTA_TEXT } from "../../data";
import { Reveal, Eyebrow, H2 } from "./Typo";
import SystemFinder from "../SystemFinder";

const SYSTEMS = [
  { id: "complete", tag: BEST_SELLER.tag, name: BEST_SELLER.name, image: BEST_SELLER.image, kind: "Whole-home filtration, softening & drinking water",
    text: "Our most complete package: a layered whole-home tank that filters and softens every tap, paired with a reverse osmosis drinking water system for your kitchen.",
    points: BEST_SELLER.contents },
  ...PRODUCT_FEATURES.map((p) => ({ id: p.id, tag: p.tag, name: p.title, image: p.image, text: p.text, points: p.benefits,
    kind: p.id === "well-water" ? "Well water treatment & softening" : "Dual tank filtration & softening" })),
];

const SystemRow = ({ s, i }) => {
  const flip = i % 2 === 1;
  return (
    <article id={`system-${s.id}`} data-testid={`system-${s.id}`} className="scroll-mt-24 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center py-16 md:py-24 border-t border-[#D9D6CC]">
      <Reveal className={`lg:col-span-6 ${flip ? "lg:order-2 lg:col-start-7" : ""}`}>
        <div className="bg-white aspect-[5/4] flex items-center justify-center p-8 md:p-12">
          <img src={s.image} alt={s.name} loading="lazy" className="max-h-full max-w-full object-contain" />
        </div>
      </Reveal>
      <Reveal delay={0.1} className={`lg:col-span-5 ${flip ? "lg:order-1" : "lg:col-start-8"}`}>
        <p className="text-sm text-[#344C59]"><span className="font-medium">{s.tag}</span> <span className="text-[#8a9195]">·</span> {s.kind}</p>
        <h3 className="font-heading text-[2.2rem] md:text-[2.8rem] leading-[1.08] text-[#172B38] mt-4">{s.name}</h3>
        <p className="mt-5 text-[1rem] leading-relaxed text-[#3E474B]">{s.text}</p>
        <ul className="mt-7 border-t border-[#D9D6CC]">
          {s.points.slice(0, 5).map((pt) => (
            <li key={pt} className="py-3 border-b border-[#D9D6CC] text-[0.93rem] text-[#252B2E]">{pt}</li>
          ))}
        </ul>
        <div className="mt-8 flex flex-wrap items-center gap-6">
          <a href="#schedule" data-testid={`system-${s.id}-cta`} className="btn-cta">{CTA_TEXT}</a>
          <Link to="/systems-explained" className="inline-flex items-center gap-2 text-[0.92rem] text-[#172B38] border-b border-[#172B38] pb-0.5 hover:text-[#344C59]">How it works <ArrowRight className="w-3.5 h-3.5" /></Link>
        </div>
      </Reveal>
    </article>
  );
};

export default function Systems({ bare = false }) {
  return (
    <section id="systems" data-testid="systems-section" className="bg-[#F4F1E9] px-6 md:px-12 pb-8">
      <div className="container-hw">
        {!bare && <div className="grid lg:grid-cols-12 gap-8 pt-4 pb-16 md:pb-20">
          <Reveal className="lg:col-span-7">
            <Eyebrow>Our systems</Eyebrow>
            <H2 className="mt-6">Water Treatment, Designed Around Your Home.</H2>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-4 lg:col-start-9 self-end">
            <p className="text-[1.05rem] leading-relaxed text-[#3E474B]">There's no one-size-fits-all solution. We help identify your home's water concerns and recommend a system suited to your needs.</p>
          </Reveal>
        </div>}
        <SystemFinder />
        {SYSTEMS.map((s, i) => <SystemRow key={s.id} s={s} i={i} />)}
      </div>

      <div className="container-hw bg-[#344C59] text-[#F4F1E9] grid md:grid-cols-12 items-stretch mb-16" data-testid="ro-band">
        <div className="md:col-span-5 bg-[#2c424e] flex items-center justify-center p-10">
          <img src={IMAGES.prodDrinking} alt="Tankless reverse osmosis drinking water system with alkaline stage" loading="lazy" className="max-h-[320px] object-contain" />
        </div>
        <div className="md:col-span-7 p-8 md:p-14 flex flex-col justify-center">
          <Eyebrow light>Drinking water</Eyebrow>
          <h3 className="font-heading text-[2.2rem] md:text-[2.8rem] leading-[1.08] mt-5">Reverse Osmosis, at the Kitchen Tap.</h3>
          <p className="mt-5 text-[1rem] leading-relaxed text-[#F4F1E9]/80 max-w-xl">A compact, tankless under-sink system dedicated to drinking and cooking water. Seven stages of filtration, followed by an alkaline stage that adds beneficial minerals back. Performance depends on the specific system and its rated reduction claims, which we'll review with you.</p>
          <div className="mt-8"><Link to="/systems-explained" className="btn-ghost-light">How our systems work</Link></div>
        </div>
      </div>
    </section>
  );
}
